// @ts-nocheck
import { Component } from '@theme/component';
import {
  getApiEndpoint,
  buildFieldMask,
  STORE_LIST_FIELDS,
  getStoreHoursStatus,
  formatAddress,
  formatPhoneHref,
  formatOpeningHours,
  getDirectionsUrl,
  sortStoresByDistance,
  sortStoresAlphabetically,
  getCachedStores,
  setCachedStores,
  getUserGeolocation,
  getStoreDetailEndpoint,
} from './store-locator-helpers.js';

/**
 * Clean store name by removing brand prefix
 * @param {string} name - Original store name
 * @returns {string} - Cleaned store name
 */
function cleanStoreName(name) {
  if (!name) return '';
  return name.replace(/^Veronica Beard\s*[-–—]?\s*/i, '').trim();
}

/**
 * Get custom field value from store
 * @param {Object} store - Store data
 * @param {string} fieldKey - The custom field key to look for
 * @returns {string|null} - The field value or null
 */
function getCustomField(store, fieldKey) {
  if (!store.customFields || !Array.isArray(store.customFields)) return null;
  const field = store.customFields.find((f) => f.key === fieldKey || f.name === fieldKey);
  return field?.value || field?.text || null;
}

/**
 * Grayscale map styles for Google Maps
 */
const GRAYSCALE_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

/**
 * Custom marker SVG icons
 */
const MARKER_ICON = {
  default: `<svg width="37" height="55" viewBox="0 0 37 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18.5" cy="18.5" r="18.5" fill="black"/>
    <path d="M18.4999 55L34.9999 27H2.07887L18.4999 55Z" fill="black"/>
    <path d="M18.409 13.6L15.541 22H14.221L11.353 13.6H12.769L14.881 20.152L16.993 13.6H18.409ZM19.3585 22V13.6H22.3345C23.7745 13.6 24.6865 14.392 24.6865 15.796C24.6865 16.456 24.3265 17.164 23.8345 17.416C24.6385 17.752 25.1185 18.556 25.1185 19.528C25.1185 21.1 24.1465 22 22.4785 22H19.3585ZM22.2145 14.848H20.6785V16.912H22.2145C22.9825 16.912 23.3665 16.564 23.3665 15.88C23.3665 15.196 22.9825 14.848 22.2145 14.848ZM22.2985 18.16H20.6785V20.752H22.2985C23.2825 20.752 23.7745 20.32 23.7745 19.456C23.7745 18.592 23.2825 18.16 22.2985 18.16Z" fill="white"/>
    </svg>
  `,
  hover: `<svg width="37" height="55" viewBox="0 0 37 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18.5" cy="18.5" r="18.5" fill="#767676"/>
    <path d="M18.4999 55L34.9999 27H2.07887L18.4999 55Z" fill="#767676"/>
    <path d="M18.409 13.6L15.541 22H14.221L11.353 13.6H12.769L14.881 20.152L16.993 13.6H18.409ZM19.3585 22V13.6H22.3345C23.7745 13.6 24.6865 14.392 24.6865 15.796C24.6865 16.456 24.3265 17.164 23.8345 17.416C24.6385 17.752 25.1185 18.556 25.1185 19.528C25.1185 21.1 24.1465 22 22.4785 22H19.3585ZM22.2145 14.848H20.6785V16.912H22.2145C22.9825 16.912 23.3665 16.564 23.3665 15.88C23.3665 15.196 22.9825 14.848 22.2145 14.848ZM22.2985 18.16H20.6785V20.752H22.2985C23.2825 20.752 23.7745 20.32 23.7745 19.456C23.7745 18.592 23.2825 18.16 22.2985 18.16Z" fill="white"/>
    </svg>
  `,
  selected: `<svg width="37" height="55" viewBox="0 0 37 55" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="18.5" cy="18.5" r="18.5" fill="#767676"/>
    <path d="M18.4999 55L34.9999 27H2.07887L18.4999 55Z" fill="#767676"/>
    <path d="M18.409 13.6L15.541 22H14.221L11.353 13.6H12.769L14.881 20.152L16.993 13.6H18.409ZM19.3585 22V13.6H22.3345C23.7745 13.6 24.6865 14.392 24.6865 15.796C24.6865 16.456 24.3265 17.164 23.8345 17.416C24.6385 17.752 25.1185 18.556 25.1185 19.528C25.1185 21.1 24.1465 22 22.4785 22H19.3585ZM22.2145 14.848H20.6785V16.912H22.2145C22.9825 16.912 23.3665 16.564 23.3665 15.88C23.3665 15.196 22.9825 14.848 22.2145 14.848ZM22.2985 18.16H20.6785V20.752H22.2985C23.2825 20.752 23.7745 20.32 23.7745 19.456C23.7745 18.592 23.2825 18.16 22.2985 18.16Z" fill="white"/>
    </svg>
  `,
};

/**
 * Create a Google Maps marker icon from SVG content
 * @param {string} svgContent - SVG markup
 * @param {number} [size=32] - Icon width (height auto-calculated as 1.25x)
 * @returns {google.maps.Icon} Marker icon configuration
 */
function createMarkerIcon(svgContent, size = 32) {
  const height = Math.round(size * 1.25);
  return {
    url: 'data:image/svg+xml,' + encodeURIComponent(svgContent),
    scaledSize: new google.maps.Size(size, height),
    anchor: new google.maps.Point(size / 2, height),
  };
}

/**
 * Store Locator Web Component
 * Displays a list of stores with Google Maps integration
 */
class StoreLocator extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {google.maps.Map | null} */
  #map = null;

  /** @type {google.maps.Marker[]} */
  #markers = [];

  /** @type {Map<number, google.maps.Marker>} */
  #markerMap = new Map();

  /** @type {google.maps.Marker | null} */
  #userMarker = null;

  /** @type {google.maps.InfoWindow | null} */
  #infoWindow = null;

  /** @type {any} */
  #clusterer = null;

  /** @type {google.maps.LatLngBounds | null} */
  #bounds = null;

  /** @type {number | null} */
  #searchTimeout = null;

  /** @type {Object[]} */
  #stores = [];

  /** @type {Object[]} */
  #allStores = [];

  /** @type {{lat: number, lng: number} | null} */
  #userLocation = null;

  /** @type {number | null} */
  #selectedStoreId = null;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {boolean} */
  #viewportFilterEnabled = false;

  /** @type {boolean} */
  #programmaticMove = false;

  /** @type {Map<number, string[]>} */
  #servicesCache = new Map();

  /** @type {Object|null} */
  #translationsCache = null;

  connectedCallback() {
    super.connectedCallback();

    // Use IntersectionObserver for lazy initialization
    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.#isInitialized) {
          this.#initialize();
          this.#intersectionObserver?.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up all resources
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#infoWindow?.close();

    if (this.#searchTimeout) {
      clearTimeout(this.#searchTimeout);
    }

    // Clear clusterer
    if (this.#clusterer) {
      this.#clusterer.clearMarkers();
      this.#clusterer = null;
    }

    // Remove markers
    this.#markers.forEach((marker) => {
      marker.setMap(null);
    });
    this.#markers = [];
    this.#markerMap.clear();

    // Remove user marker
    if (this.#userMarker) {
      this.#userMarker.setMap(null);
      this.#userMarker = null;
    }
  }

  /**
   * Initialize the store locator
   */
  async #initialize() {
    this.#isInitialized = true;
    this.#showLoading();

    // Try to load cached data first for instant display
    const cached = getCachedStores();
    if (cached) {
      this.#allStores = cached;
      this.#stores = sortStoresAlphabetically(cached);
      this.#renderStoreList();
    }

    // Fetch fresh data
    await this.#fetchStores();

    // Load Google Maps, then initialize
    await this.#loadGoogleMaps();
  }

  /**
   * Load Google Maps API dynamically
   */
  async #loadGoogleMaps() {
    const mapsKey = this.dataset.mapsKey;
    if (!mapsKey) return;

    // Check if already loaded
    if (window.google?.maps?.Map) {
      await this.#initializeMap();
      return;
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsKey}&callback=__storeLocatorMapsCallback`;
      script.async = true;

      window.__storeLocatorMapsCallback = async () => {
        delete window.__storeLocatorMapsCallback;
        await this.#initializeMap();
        resolve();
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Fetch stores from Uberall API
   */
  async #fetchStores() {
    const apiKey = this.dataset.apiKey;
    if (!apiKey) {
      this.#showError();
      return;
    }

    // Create new abort controller for this request
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const endpoint = getApiEndpoint(apiKey);
    const fieldMask = buildFieldMask(STORE_LIST_FIELDS);

    try {
      const response = await fetch(`${endpoint}${fieldMask}`, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      const locations = data.response?.locations || [];
      this.#allStores = locations;
      // Default to alphabetical sort until geolocation resolves
      this.#stores = sortStoresAlphabetically(locations);

      // Cache the response
      setCachedStores(locations);

      this.#renderStoreList();
      this.#hideLoading();
    } catch (error) {
      if (error.name === 'AbortError') return;

      // Only show error if we don't have cached data
      if (!this.#stores.length) {
        this.#showError();
      }
    }
  }

  /**
   * Initialize Google Map instance
   */
  async #initializeMap() {
    const mapContainer = this.refs.mapContainer;
    if (!mapContainer || !window.google?.maps) return;

    // Default center (world view)
    const defaultCenter = { lat: 20, lng: 0 };
    const defaultZoom = parseInt(this.dataset.defaultZoom, 10) || 2;

    this.#map = new google.maps.Map(mapContainer, {
      center: defaultCenter,
      zoom: defaultZoom,
      styles: GRAYSCALE_MAP_STYLES,
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      scaleControl: false,
      rotateControl: false,
      keyboardShortcuts: true,
      gestureHandling: 'cooperative',
    });

    this.#infoWindow = new google.maps.InfoWindow();
    this.#bounds = new google.maps.LatLngBounds();

    // Add markers for all stores
    this.#addMarkers();

    // Set up marker clustering
    this.#setupClustering();

    // Set up map event listeners
    this.#setupMapListeners();

    // Auto-get user location
    await this.#autoLocateUser();
  }

  /**
   * Add markers for all stores
   */
  #addMarkers() {
    if (!this.#map) return;

    // Clear existing markers
    this.#markers.forEach((marker) => marker.setMap(null));
    this.#markers = [];
    this.#markerMap.clear();
    this.#bounds = new google.maps.LatLngBounds();

    this.#allStores.forEach((store) => {
      const position = { lat: store.lat, lng: store.lng };

      // Create marker element
      const markerContent = document.createElement('div');
      markerContent.className = 'store-locator__marker';
      markerContent.innerHTML = MARKER_ICON.default;

      const marker = new google.maps.Marker({
        position,
        map: this.#map,
        title: cleanStoreName(store.name),
        icon: createMarkerIcon(MARKER_ICON.default),
      });

      // Store reference to store ID
      marker.storeId = store.id;

      // Hover effects
      marker.addListener('mouseover', () => {
        if (this.#selectedStoreId !== store.id) {
          marker.setIcon(createMarkerIcon(MARKER_ICON.hover));
        }
      });

      marker.addListener('mouseout', () => {
        if (this.#selectedStoreId !== store.id) {
          marker.setIcon(createMarkerIcon(MARKER_ICON.default));
        }
      });

      // Click handler
      marker.addListener('click', () => {
        this.#selectStore(store.id);
      });

      this.#markers.push(marker);
      this.#markerMap.set(store.id, marker);
      this.#bounds.extend(position);
    });
  }

  /**
   * Set up marker clustering
   */
  #setupClustering() {
    if (!this.#map || !window.markerClusterer) {
      return;
    }

    const { MarkerClusterer, SuperClusterAlgorithm } = window.markerClusterer;

    const algorithm = new SuperClusterAlgorithm({
      maxZoom: 12,
      radius: 100,
    });

    const renderer = {
      render: ({ count, position }) => {
        return new google.maps.Marker({
          position,
          label: {
            text: String(count),
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'bold',
          },
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 20,
            fillColor: '#000',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 2,
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };

    this.#clusterer = new MarkerClusterer({
      map: this.#map,
      markers: this.#markers,
      algorithm,
      renderer,
    });
  }

  /**
   * Set up map event listeners
   */
  #setupMapListeners() {
    if (!this.#map) return;

    // Close info window on map click
    this.#map.addListener('click', () => {
      this.#closeInfoWindow();
    });

    // Re-enable viewport filtering on manual user interaction (drag/zoom)
    this.#map.addListener('dragstart', () => {
      this.#viewportFilterEnabled = true;
    });

    this.#map.addListener('zoom_changed', () => {
      if (!this.#programmaticMove) {
        this.#viewportFilterEnabled = true;
      }
    });

    // Viewport filtering - update store list based on visible markers
    this.#map.addListener('idle', () => {
      if (this.#viewportFilterEnabled) {
        this.#updateVisibleStores();
      }
    });
  }

  /**
   * Update store list based on visible markers in viewport
   */
  #updateVisibleStores() {
    if (!this.#map) return;

    const bounds = this.#map.getBounds();
    if (!bounds) return;

    const visibleStores = this.#allStores.filter((store) => {
      const position = new google.maps.LatLng(store.lat, store.lng);
      return bounds.contains(position);
    });

    // Sort by distance if user location available, otherwise alphabetically
    if (this.#userLocation) {
      this.#stores = sortStoresByDistance(visibleStores, this.#userLocation);
    } else {
      this.#stores = sortStoresAlphabetically(visibleStores);
    }

    this.#renderStoreList();
  }

  /**
   * Close info window and reset selection
   */
  #closeInfoWindow() {
    this.#infoWindow?.close();

    // Reset selected marker icon
    if (this.#selectedStoreId) {
      const marker = this.#markerMap.get(this.#selectedStoreId);
      if (marker) {
        marker.setIcon(createMarkerIcon(MARKER_ICON.default));
      }
    }

    this.#selectedStoreId = null;

    // Remove selection from list
    const cards = this.refs.storeList?.querySelectorAll('.is-selected');
    cards?.forEach((card) => card.classList.remove('is-selected'));
  }

  /**
   * Automatically get user location and center map
   */
  async #autoLocateUser() {
    try {
      this.#userLocation = await getUserGeolocation();

      // Sort stores by distance from user
      this.#stores = sortStoresByDistance(this.#allStores, this.#userLocation);
      this.#renderStoreList();

      // Add user location marker
      this.#addUserMarker();

      // Zoom to nearest store, then enable viewport filtering once map settles
      this.#fitToNearestStore();
      this.#enableViewportFilterOnIdle();
    } catch {
      // No geolocation available - sort stores alphabetically and enable viewport filtering
      this.#stores = sortStoresAlphabetically(this.#allStores);
      this.#renderStoreList();
      this.#viewportFilterEnabled = true;
      this.#focusWorldView();
    }
  }

  /**
   * Enable viewport filtering after the next map idle event
   */
  #enableViewportFilterOnIdle() {
    if (!this.#map) return;

    const listener = this.#map.addListener('idle', () => {
      google.maps.event.removeListener(listener);
      this.#viewportFilterEnabled = true;
      this.#updateVisibleStores();
    });
  }

  /**
   * Focus map to show all stores (fallback when no user location)
   */
  #focusWorldView() {
    if (!this.#map) return;

    this.#programmaticMove = true;

    if (this.#bounds && !this.#bounds.isEmpty()) {
      this.#map.fitBounds(this.#bounds);
    } else {
      this.#map.setCenter({ lat: 20, lng: 0 });
      this.#map.setZoom(2);
    }

    this.#programmaticMove = false;
  }

  /**
   * Zoom into the nearest store
   */
  #fitToNearestStore() {
    if (!this.#map || !this.#stores.length) return;

    this.#programmaticMove = true;
    const nearest = this.#stores[0];
    this.#map.setCenter({ lat: nearest.lat, lng: nearest.lng });
    this.#map.setZoom(12);
    this.#programmaticMove = false;
  }

  /**
   * Add marker for user's current location
   */
  #addUserMarker() {
    if (!this.#map || !this.#userLocation) return;

    // Remove existing user marker
    if (this.#userMarker) {
      this.#userMarker.setMap(null);
    }

    // User location marker - blue dot style
    const userMarkerSvg = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="rgba(66, 133, 244, 0.2)"/>
      <circle cx="12" cy="12" r="6" fill="#4285f4" stroke="#fff" stroke-width="2"/>
    </svg>`;

    this.#userMarker = new google.maps.Marker({
      map: this.#map,
      position: this.#userLocation,
      title: 'Your location',
      icon: {
        url: 'data:image/svg+xml,' + encodeURIComponent(userMarkerSvg),
        scaledSize: new google.maps.Size(24, 24),
        anchor: new google.maps.Point(12, 12),
      },
      zIndex: 1000,
    });
  }

  /**
   * Select a store and show info window
   * @param {number} storeId
   */
  #selectStore(storeId) {
    // Close previous selection
    this.#closeInfoWindow();

    this.#selectedStoreId = storeId;
    const store = this.#allStores.find((s) => s.id === storeId);
    if (!store) return;

    // Update marker icon to selected state
    const marker = this.#markerMap.get(storeId);
    if (marker) {
      marker.setIcon(createMarkerIcon(MARKER_ICON.selected));
    }

    // Update list selection
    this.#updateListSelection(storeId);

    // Show info window on map
    if (this.#map && this.#infoWindow) {
      const translations = this.#getTranslations();
      const status = getStoreHoursStatus(store, translations);

      this.#infoWindow.setContent(`
        <div class="store-locator__info-window">
          <strong>${cleanStoreName(store.name)}</strong>
          <p>${formatAddress(store)}</p>
          <p class="store-locator__status ${status.isOpen ? 'is-open' : ''}">${status.text}</p>
          <a href="${getDirectionsUrl(store)}" target="_blank" rel="noopener">${translations.get_directions}</a>
        </div>
      `);

      this.#infoWindow.setPosition({ lat: store.lat, lng: store.lng });
      this.#infoWindow.open(this.#map);

      // Center map on selected store
      this.#programmaticMove = true;
      this.#map.panTo({ lat: store.lat, lng: store.lng });
      this.#map.setZoom(Math.max(this.#map.getZoom() || 10, 10));
      this.#programmaticMove = false;
    }
  }

  /**
   * Update visual selection state in store list
   * @param {number} storeId
   */
  #updateListSelection(storeId) {
    const cards = this.refs.storeList?.querySelectorAll('[data-store-id]');
    cards?.forEach((card) => {
      card.classList.toggle('is-selected', card.dataset.storeId === String(storeId));
    });

    // Scroll selected card into view
    const selectedCard = this.refs.storeList?.querySelector(`[data-store-id="${storeId}"]`);
    selectedCard?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Handle search input with debounce
   * @param {Event} event
   */
  handleSearchInput = (event) => {
    const value = event.target.value;

    // Show/hide clear button
    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = !value;
    }

    if (this.#searchTimeout) {
      clearTimeout(this.#searchTimeout);
    }

    this.#searchTimeout = setTimeout(() => {
      this.#filterStores(value);
    }, 300);
  };

  /**
   * Handle clear search button click
   */
  handleClearSearch = () => {
    const input = this.refs.searchInput;
    if (input) {
      input.value = '';
      input.focus();
    }

    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = true;
    }

    this.#filterStores('');
  };

  /**
   * Geocode a search query to get coordinates
   * @param {string} query - Address or postal code to geocode
   * @returns {Promise<{lat: number, lng: number}|null>} Coordinates or null if geocoding fails
   */
  async #geocodeQuery(query) {
    if (!window.google?.maps?.Geocoder) return null;

    const geocoder = new google.maps.Geocoder();

    return new Promise((resolve) => {
      geocoder.geocode({ address: query }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          resolve({ lat: location.lat(), lng: location.lng() });
        } else {
          resolve(null);
        }
      });
    });
  }

  /**
   * Filter stores by search query
   * @param {string} query
   */
  async #filterStores(query) {
    const normalizedQuery = query.toLowerCase().trim();
    // Normalize query for postcode comparison (remove spaces)
    const normalizedQueryNoSpaces = normalizedQuery.replace(/\s+/g, '');

    // Disable viewport filtering during search
    this.#viewportFilterEnabled = !normalizedQuery;

    if (!normalizedQuery) {
      // Clear search marker when search is cleared


      // Reset to all stores - sorted by distance if location available, otherwise alphabetically
      this.#stores = this.#userLocation
        ? sortStoresByDistance(this.#allStores, this.#userLocation)
        : sortStoresAlphabetically(this.#allStores);
      this.#renderStoreList();

      // Show all markers
      this.#markers.forEach((marker) => marker.setVisible(true));
      if (this.#clusterer) {
        this.#clusterer.clearMarkers();
        this.#clusterer.addMarkers(this.#markers);
      }
      return;
    }

    // Text-based filtering with normalized postcodes
    const filtered = this.#allStores.filter((store) => {
      // Standard text matching
      const searchableText = [store.name, store.city, store.province, store.zip, store.streetAndNumber]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (searchableText.includes(normalizedQuery)) {
        return true;
      }

      // Also try matching zip code without spaces (for UK postcodes like "SW1A 1AA" vs "SW1A1AA")
      if (store.zip) {
        const zipNoSpaces = store.zip.replace(/\s+/g, '').toLowerCase();
        if (zipNoSpaces.includes(normalizedQueryNoSpaces) || normalizedQueryNoSpaces.includes(zipNoSpaces)) {
          return true;
        }
      }

      return false;
    });

    // If we have results from text matching, show them
    if (filtered.length > 0) {

      this.#stores = filtered;
      this.#renderStoreList();

      // Update markers visibility
      const filteredIds = new Set(filtered.map((s) => s.id));
      this.#markers.forEach((marker) => {
        marker.setVisible(filteredIds.has(marker.storeId));
      });

      // Update clusterer
      if (this.#clusterer) {
        this.#clusterer.clearMarkers();
        this.#clusterer.addMarkers(this.#markers.filter((m) => filteredIds.has(m.storeId)));
      }

      // Fit to visible markers
      if (this.#map) {
        this.#programmaticMove = true;
        const bounds = new google.maps.LatLngBounds();
        filtered.forEach((store) => bounds.extend({ lat: store.lat, lng: store.lng }));
        this.#map.fitBounds(bounds);
        this.#programmaticMove = false;
      }
      return;
    }

    // No text matches - try geocoding the query to find nearby stores
    if (normalizedQuery.length >= 3) {
      const location = await this.#geocodeQuery(query);

      if (location) {
        // Sort all stores by distance from geocoded location
        this.#stores = sortStoresByDistance([...this.#allStores], location);
        this.#renderStoreList();

        // Show all markers
        this.#markers.forEach((marker) => marker.setVisible(true));
        if (this.#clusterer) {
          this.#clusterer.clearMarkers();
          this.#clusterer.addMarkers(this.#markers);
        }

        // Center map on geocoded location
        if (this.#map) {
          this.#programmaticMove = true;
          this.#map.setCenter(location);
          this.#map.setZoom(10);
          this.#programmaticMove = false;
        }
        return;
      }
    }

    // No results and geocoding failed - show empty state
    this.#stores = [];
    this.#renderStoreList();

    // Hide all markers
    this.#markers.forEach((marker) => marker.setVisible(false));
    if (this.#clusterer) {
      this.#clusterer.clearMarkers();
    }
  }

  /**
   * Handle "Use my location" button click
   */
  handleLocationClick = async () => {
    const button = this.refs.locationButton;
    if (!button) return;

    button.disabled = true;
    button.classList.add('is-loading');

    try {
      this.#userLocation = await getUserGeolocation();

      // Sort stores by distance
      this.#stores = sortStoresByDistance(this.#allStores, this.#userLocation);
      this.#renderStoreList();

      // Add user marker if not already added
      if (!this.#userMarker) {
        this.#addUserMarker();
      }

      // Fit map to show user location and nearest store
      this.#fitToNearestStore();
      this.#enableViewportFilterOnIdle();
    } catch {
      // Geolocation failed - no action needed
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  };

  /**
   * Handle store card click
   */
  handleStoreClick = (data, event) => {
    event.preventDefault();
    this.#selectStore(data.id);
  };

  /**
   * Handle retry button click
   */
  handleRetryClick = () => {
    this.#showLoading();
    this.#fetchStores();
  };

  /**
   * Handle accordion toggle to load services
   */
  handleAccordionToggle = async (event) => {
    const details = event.target;
    if (!details.open) return;

    const storeId = parseInt(details.dataset.storeId, 10);
    if (!storeId) return;

    const servicesContainer = details.querySelector('[data-services-container]');
    if (!servicesContainer || servicesContainer.dataset.loaded) return;

    // Check cache first
    if (this.#servicesCache.has(storeId)) {
      this.#renderServices(servicesContainer, this.#servicesCache.get(storeId));
      return;
    }

    // Show shimmer loading state
    servicesContainer.innerHTML = `
      <ul class="store-locator__services-list is-loading">
        <li class="store-locator__services-shimmer"></li>
        <li class="store-locator__services-shimmer"></li>
        <li class="store-locator__services-shimmer"></li>
      </ul>
    `;

    try {
      const storeKey = this.dataset.apiKey;
      const endpoint = getStoreDetailEndpoint(storeKey, storeId);
      const response = await fetch(`${endpoint}?fieldMask=services`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      const services = result.response?.services || [];

      // Cache the result
      this.#servicesCache.set(storeId, services);

      this.#renderServices(servicesContainer, services);
    } catch {
      servicesContainer.innerHTML = '';
    }
  };

  /**
   * Render services list
   * @param {HTMLElement} container
   * @param {string[]} services
   */
  #renderServices(container, services) {
    container.dataset.loaded = 'true';

    if (!services || services.length === 0) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = `
      <ul class="store-locator__services-list">
        ${services.map((service) => `<li>${service}</li>`).join('')}
      </ul>
    `;
  }

  /**
   * Render the store list
   * @param {Object[]} [stores]
   */
  #renderStoreList(stores = this.#stores) {
    const container = this.refs.storeList;
    if (!container) return;

    const translations = this.#getTranslations();

    // Update store count
    this.#updateStoreCount(stores.length);

    if (!stores.length) {
      container.innerHTML = `
        <div class="store-locator__empty">
          <p>${translations.no_stores_found}</p>
        </div>
      `;
      return;
    }

    const fragment = document.createDocumentFragment();

    stores.forEach((store) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'store-locator__card';
      if (this.#selectedStoreId === store.id) {
        card.classList.add('is-selected');
      }
      card.dataset.storeId = store.id;
      card.setAttribute('on:click', `/handleStoreClick?id=${store.id}`);

      const status = getStoreHoursStatus(store, translations);
      const cityLine = [store.city, store.province, store.zip].filter(Boolean).join(', ');
      const storePageUrl = getCustomField(store, 'shopify_store_page_url') || store.shopify_store_page_url;
      const openingHours = formatOpeningHours(store.openingHours, translations.days);

      const hoursHtml = openingHours.length
        ? `
        <details class="store-locator__accordion" data-store-id="${store.id}" on:click="stopPropagation" on:toggle="/handleAccordionToggle">
          <summary class="store-locator__accordion-header">
            <span>${translations.services_hours}</span>
            <span class="store-locator__accordion-icon">
              <svg class="icon-plus" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              <svg class="icon-minus" width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 6h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </span>
          </summary>
          <div class="store-locator__accordion-content">
            <div class="store-locator__hours">
              ${openingHours.map((line) => `<p>${line}</p>`).join('')}
            </div>
            <div class="store-locator__services" data-services-container></div>
          </div>
        </details>
      `
        : '';

      card.innerHTML = `
        <div class="store-locator__card-header">
          <h3 class="store-locator__card-name h2">${cleanStoreName(store.name)}</h3>
          <p class="store-locator__card-status ${status.isOpen ? 'is-open' : ''}">${status.text}</p>
        </div>
        <address class="store-locator__card-address">
          ${store.streetAndNumber ? `<span class="store-locator__card-street">${store.streetAndNumber}</span>` : ''}
          ${cityLine ? `<span class="store-locator__card-city">${cityLine}</span>` : ''}
          ${store.phone ? `<a href="tel:${formatPhoneHref(store.phone)}" class="store-locator__card-phone" on:click="stopPropagation">${store.phone}</a>` : ''}
        </address>
        ${hoursHtml}
        <div class="store-locator__card-actions">
          <a href="${getDirectionsUrl(store)}" target="_blank" rel="noopener" class="store-locator__card-link button button-primary" on:click="stopPropagation">
            ${translations.get_directions}
          </a>
          ${storePageUrl ? `<a href="${storePageUrl}" class="store-locator__card-link button button-secondary" on:click="stopPropagation">${translations.view_store}</a>` : ''}
        </div>
      `;

      fragment.appendChild(card);
    });

    container.innerHTML = '';
    container.appendChild(fragment);
  }

  /**
   * Update store count display
   * @param {number} visibleCount
   */
  #updateStoreCount(visibleCount) {
    const countEl = this.refs.storeCount;
    const viewAllBtn = this.refs.viewAllButton;
    const translations = this.#getTranslations();
    const totalCount = this.#allStores.length;

    if (countEl) {
      const template = visibleCount === 1 ? translations.location : translations.locations;
      countEl.textContent = template.replace('{{ count }}', visibleCount);
    }

    // Show "View All" button if not showing all stores
    if (viewAllBtn) {
      if (visibleCount < totalCount && totalCount > 0) {
        viewAllBtn.textContent = translations.view_all.replace('{{ count }}', totalCount);
        viewAllBtn.removeAttribute('hidden');
      } else {
        viewAllBtn.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Handle "View All" button click
   */
  handleViewAllClick = () => {
    // Clear search input
    const input = this.refs.searchInput;
    if (input) {
      input.value = '';
    }
    if (this.refs.clearButton) {
      this.refs.clearButton.hidden = true;
    }

    // Disable viewport filtering
    this.#viewportFilterEnabled = false;

    // Show all stores sorted by distance if available, otherwise alphabetically
    this.#stores = this.#userLocation
      ? sortStoresByDistance([...this.#allStores], this.#userLocation)
      : sortStoresAlphabetically(this.#allStores);

    this.#renderStoreList();

    // Fit map to show all stores
    if (this.#map && this.#bounds) {
      this.#programmaticMove = true;
      this.#map.fitBounds(this.#bounds);
      this.#programmaticMove = false;
    }

    // Show all markers
    this.#markers.forEach((marker) => marker.setVisible(true));
    if (this.#clusterer) {
      this.#clusterer.clearMarkers();
      this.#clusterer.addMarkers(this.#markers);
    }
  };

  /**
   * Stop event propagation
   */
  stopPropagation = (event) => {
    event.stopPropagation();
  };

  /**
   * Show loading state
   */
  #showLoading() {
    this.refs.loading?.removeAttribute('hidden');
    this.refs.error?.setAttribute('hidden', '');
  }

  /**
   * Hide loading state
   */
  #hideLoading() {
    this.refs.loading?.setAttribute('hidden', '');
  }

  /**
   * Show error state
   */
  #showError() {
    this.refs.loading?.setAttribute('hidden', '');
    this.refs.error?.removeAttribute('hidden');
  }

  /**
   * Get translation strings from data attributes (cached)
   */
  #getTranslations() {
    if (this.#translationsCache) return this.#translationsCache;

    const defaultDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daysString = this.dataset.tDays;
    const days = daysString ? daysString.split(',') : defaultDays;

    this.#translationsCache = {
      open_now: this.dataset.tOpenNow || 'Open now',
      open_until: this.dataset.tOpenUntil || 'Open until {{ time }}',
      opens_at: this.dataset.tOpensAt || 'Opens at {{ time }}',
      closed: this.dataset.tClosed || 'Closed',
      temporarily_closed: this.dataset.tTemporarilyClosed || 'Temporarily closed',
      coming_soon: this.dataset.tComingSoon || 'Coming soon {{ date }}',
      get_directions: this.dataset.tGetDirections || 'Get directions',
      view_store: this.dataset.tViewStore || 'View store',
      services_hours: this.dataset.tServicesHours || 'Services & hours',
      services: this.dataset.tServices || 'Services',
      loading: this.dataset.tLoading || 'Loading...',
      call: this.dataset.tCall || 'Call',
      miles_away: this.dataset.tMilesAway || '{{ distance }} mi away',
      no_stores_found: this.dataset.tNoStoresFound || 'No stores found',
      locations: this.dataset.tLocations || '{{ count }} locations',
      location: this.dataset.tLocation || '{{ count }} location',
      view_all: this.dataset.tViewAll || 'View all {{ count }} stores',
      days: ['', ...days], // Index 0 is empty, 1-7 are Monday-Sunday
    };

    return this.#translationsCache;
  }
}

if (!customElements.get('store-locator')) {
  customElements.define('store-locator', StoreLocator);
}
